import type { GetConfigData, SaveConfigRequest } from '@legalhermes/shared/api-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Mail, Save } from 'lucide-react';
import { type ChangeEvent, type ReactNode, useState } from 'react';
import { queryKeys } from '../lib/queryKeys';
import { cn } from '../lib/utils';
import { api } from '../services/api';
import PageLoader from './PageLoader';

/**
 * Config tal como lo consume esta pantalla: campos reales del backend
 * (GetConfigData). El índice extra tolera claves adicionales sin tipar
 * que puedan llegar del backend sin romper el acceso.
 */
type LocalConfig = Partial<GetConfigData> &
  Record<string, string | number | boolean | null | undefined>;

interface ToggleProps {
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  label: string;
  hint?: string;
}

/* ── Toggle switch component ── */
function Toggle({ checked, onChange, label, hint }: ToggleProps) {
  return (
    <label className="flex items-start gap-4 px-5 py-4 rounded-md border border-transparent hover:bg-surface-raised transition-[background-color] duration-150 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="absolute opacity-0 w-0 h-0 pointer-events-none"
      />
      <span
        className={cn(
          'relative w-9 h-5 rounded-[10px] border transition-[background-color,border-color] duration-200 flex-shrink-0 mt-px cursor-pointer',
          "after:content-[''] after:absolute after:top-px after:left-px after:w-4 after:h-4 after:rounded-full after:transition-[transform,background-color] after:duration-200",
          checked
            ? 'bg-accent-subtle border-accent after:bg-accent'
            : 'bg-white/[0.12] border-border-emphasis after:translate-x-4 after:bg-ink-secondary'
        )}
      />
      <span className="flex flex-col gap-1 min-w-0">
        <span className="text-[0.875rem] font-medium text-ink leading-snug">{label}</span>
        {hint && <span className="text-[0.8125rem] text-ink-tertiary leading-snug">{hint}</span>}
      </span>
    </label>
  );
}

const inputClass =
  'w-full px-4 py-3 border border-border-emphasis rounded-sm text-[0.875rem] pointer-coarse:text-base font-sans bg-surface-inset text-ink placeholder-ink-muted focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(200,150,62,0.1)] transition-[border-color,box-shadow] duration-150';

interface ChildrenProps {
  children: ReactNode;
}

function SectionContainer({ children }: ChildrenProps) {
  return (
    <div className="bg-surface border border-border rounded-lg p-4 sm:p-8 mb-6 last-of-type:mb-8">
      {children}
    </div>
  );
}

interface SectionHeaderProps {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
}

function SectionHeader({ icon, title, action }: SectionHeaderProps) {
  return (
    <div className="flex justify-between items-center mb-6">
      <div className="flex items-center gap-3 font-serif text-[1.05rem] font-semibold text-ink tracking-tight">
        <span className="text-accent flex-shrink-0">{icon}</span>
        {title}
      </div>
      {action}
    </div>
  );
}

function FieldHint({ children }: ChildrenProps) {
  return (
    <span className="block mt-2 text-[0.8125rem] text-ink-tertiary leading-snug">{children}</span>
  );
}

function FormGroup({ children }: ChildrenProps) {
  return <div className="mb-6">{children}</div>;
}

function FormLabel({ children }: ChildrenProps) {
  return (
    <label className="block mb-2 font-medium text-[0.8125rem] text-ink-secondary tracking-wide">
      {children}
    </label>
  );
}

interface SubsectionProps extends ChildrenProps {
  title: string;
}

function _Subsection({ title, children }: SubsectionProps) {
  return (
    <div className="mt-6 pt-6 border-t border-border">
      <div className="text-[0.8125rem] font-semibold text-ink-secondary tracking-wide mb-5">
        {title}
      </div>
      {children}
    </div>
  );
}

function Settings() {
  const queryClient = useQueryClient();
  const [localConfig, setLocalConfig] = useState<LocalConfig | null>(null);
  const [message, setMessage] = useState('');

  const { data: remoteConfig, isLoading } = useQuery({
    queryKey: queryKeys.config,
    queryFn: api.getConfig,
  });

  const config: LocalConfig = localConfig ?? remoteConfig ?? {};

  const { mutate: saveConfig, isPending: saving } = useMutation({
    mutationFn: (data: SaveConfigRequest) => api.saveConfig(data),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.config });
        setLocalConfig(null);
        setMessage('Configuración guardada exitosamente');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`Error al guardar: ${result.error}`);
      }
    },
    onError: (err: Error) => {
      setMessage(`Error al guardar: ${err.message}`);
    },
  });

  const handleChange = (key: string, value: string | boolean) => {
    setLocalConfig((prev) => ({
      ...(prev ?? remoteConfig ?? {}),
      [key]: value,
    }));
  };

  const handleSave = () => {
    setMessage('');
    saveConfig(config as SaveConfigRequest);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div>
      {/* Page header */}
      <div className="mb-8">
        <h3 className="font-serif text-[1.35rem] font-semibold text-ink tracking-tight">
          Configuración
        </h3>
        <p className="text-ink-tertiary text-[0.875rem] mt-2 leading-snug">
          Sistema basado en APIs públicas de la Rama Judicial de Colombia
        </p>
      </div>

      {/* ── Organización ── */}
      <SectionContainer>
        <SectionHeader icon={<Building2 size={18} />} title="Organización" />

        <FormGroup>
          <FormLabel>Nombre de la Organización</FormLabel>
          <input
            type="text"
            className={cn(inputClass, 'opacity-60 cursor-not-allowed')}
            value={config.org_name || ''}
            disabled
            readOnly
          />
          <FieldHint>Nombre registrado de la organización</FieldHint>
        </FormGroup>

        <FormGroup>
          <FormLabel>NIT / RUT</FormLabel>
          <input
            type="text"
            className={cn(inputClass, 'opacity-60 cursor-not-allowed')}
            value={config.org_identifier || ''}
            disabled
            readOnly
            placeholder="900.123.456-7"
          />
          <FieldHint>Número de identificación tributaria de la organización</FieldHint>
        </FormGroup>

        <FormGroup>
          <FormLabel>Correo de la Organización</FormLabel>
          <input
            type="email"
            className={cn(inputClass, 'opacity-60 cursor-not-allowed')}
            value={config.org_email || ''}
            disabled
            readOnly
            placeholder="contacto@organización.com"
          />
          <FieldHint>Email principal de contacto de la organización</FieldHint>
        </FormGroup>
      </SectionContainer>

      {/* ── Notificaciones por Email ── */}
      <SectionContainer>
        <SectionHeader icon={<Mail size={18} />} title="Notificaciones por Email" />

        <Toggle
          checked={config.email_notifications_enabled === true}
          onChange={(e) => handleChange('email_notifications_enabled', e.target.checked)}
          label="Activar notificaciones por email"
          hint="Envía alertas al correo de la organización cuando se detecten cambios en los procesos monitoreados"
        />
        <Toggle
          checked={(config.notify_on_new_process ?? true) === true}
          onChange={(e) => handleChange('notify_on_new_process', e.target.checked)}
          label="Correo de bienvenida al detectar un proceso nuevo"
          hint={
            config.email_notifications_enabled !== true
              ? 'Las notificaciones están desactivadas. Activa el switch anterior para habilitar.'
              : 'Recibe un correo de confirmación la primera vez que detectamos un proceso para cada búsqueda.'
          }
        />
      </SectionContainer>

      {/* ── Save footer ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 py-5 border-t border-border mt-2">
        <button
          className="px-6 py-3 bg-success text-ink border-none rounded-sm text-[0.8125rem] font-medium font-sans inline-flex items-center gap-2 cursor-pointer hover:bg-success-hover transition-[background-color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={handleSave}
          disabled={saving}
        >
          <Save size={14} /> {saving ? 'Guardando...' : 'Guardar Configuración'}
        </button>

        {message && (
          <span
            className={cn(
              'text-[0.875rem] font-medium',
              message.includes('Error')
                ? 'text-destructive'
                : message.includes('Aviso')
                  ? 'text-warning'
                  : 'text-success'
            )}
          >
            {message}
          </span>
        )}
      </div>
    </div>
  );
}

export default Settings;
